// =============================================================================
// Email Express — client-side generation progress model
// -----------------------------------------------------------------------------
// Derives a live "how far along is this draft" reading PURELY from the status
// fields the background copy/image worker already persists on the email row.
// There is no timer and no new backend state: every signal here is something
// the worker writes in order, so the bar is automatically resume-safe (a page
// refresh or server restart recomputes from the same persisted row).
//
// Worker ordering the weights below mirror (see email-copy-worker.ts):
//   1. voice brief            (voiceBrief != null)
//   2. restyle + subject lines + templated group (flips body/footer/reviews/
//      logo_banner ready, writes subjectLineVariants)
//   3. hero copy (blockCopyStatus.hero ready) → kicks the hero image
//   4. optional block-copy stages, in parallel (each flips its own block ready)
//   5. the Fal image renders (hero + per-block) resolve last
// =============================================================================

import { emailBlockType } from "@shared/schema";
import type { EmailDetail } from "./email-render";

export type EmailProgressState = "generating" | "complete" | "error";

export interface EmailProgress {
  /** 0–100, monotonic in normal flow; frozen at the failure point on error. */
  percent: number;
  /** Human label for the lowest stage not yet complete. */
  phaseLabel: string;
  state: EmailProgressState;
  /**
   * Any copy or image stage is still in flight (worker not finished). Lets the
   * UI keep polling on single-block regens and know when a terminal error has
   * fully settled so the bar can fade rather than hang forever.
   */
  pending: boolean;
}

// Optional (non-locked) block types that can appear in an email. Mirrors
// OPTIONAL_ORDER in email-render but kept local so this helper has no UI deps.
const OPTIONAL_BLOCK_TYPES = new Set([
  "selling_points",
  "reviews",
  "product_list",
  "us_vs_them",
  "comparison_table",
  "did_you_know",
  "logo_banner",
]);

type ImgStatus = "pending" | "ready" | "failed" | null | undefined;

const isPending = (s: ImgStatus): boolean => s === "pending";
// "Settled OK" = this image slot is genuinely finished and not in an error
// state: either it rendered (`ready`) or none was ever kicked for this slot
// (`null`/`undefined`). A `failed` image is NOT settled-ok, so its progress
// unit stays incomplete and the bar freezes below 100% on an image failure.
const isSettledOk = (s: ImgStatus): boolean => s == null || s === "ready";

// The image status (if any) that gates a given block INSTANCE. Returns
// undefined when the block has no associated image (copy-only block).
function blockImageStatus(email: EmailDetail, instanceId: string): ImgStatus {
  const type = emailBlockType(instanceId);
  switch (type) {
    case "selling_points":
      return (email.sellingPointsImages ?? []).find(
        (i) => i.instanceId === instanceId,
      )?.status;
    case "did_you_know":
      return (email.didYouKnowImages ?? []).find(
        (i) => i.instanceId === instanceId,
      )?.status;
    case "us_vs_them":
      return email.usVsThemImageStatus;
    case "comparison_table":
      return email.comparisonTableImageStatus;
    default:
      return undefined;
  }
}

// Every image status currently tracked on the email (hero + per-block). Used to
// hold the final "Generating images…" slice until they all resolve.
function allImageStatuses(email: EmailDetail): ImgStatus[] {
  return [
    email.heroImageStatus,
    email.usVsThemImageStatus,
    email.comparisonTableImageStatus,
    ...(email.sellingPointsImages ?? []).map((i) => i.status),
    ...(email.didYouKnowImages ?? []).map((i) => i.status),
  ];
}

/**
 * Compute the live draft progress for an email from its persisted status
 * fields. Returns 100% / `complete` for legacy emails (no copyStatus) and for
 * fully-drafted emails so callers can decide whether to surface the bar at all.
 */
export function computeEmailProgress(email: EmailDetail): EmailProgress {
  const blocks = (email.blocks as string[] | null) ?? [];
  const blockCopyStatus = (email.blockCopyStatus as Record<
    string,
    "pending" | "ready" | "failed"
  > | null) ?? {};

  const optionalInstances = blocks.filter((id) =>
    OPTIONAL_BLOCK_TYPES.has(emailBlockType(id)),
  );

  const imageStatuses = allImageStatuses(email);
  const anyImagePending = imageStatuses.some(isPending);
  const allImagesResolved = !anyImagePending;

  // --- Error detection ------------------------------------------------------
  // The bar turns to its warning state when ANY stage fails — copy OR image.
  // Copy failures surface a per-block skeleton → retry; image failures render a
  // brand-color fallback. A failed image still counts as RESOLVED for the
  // percent math (so the bar freezes at the failure point rather than stalling
  // forever), but it must NOT let the draft report `complete`.
  const anyBlockCopyFailed = Object.values(blockCopyStatus).some(
    (s) => s === "failed",
  );
  const anyImageFailed = imageStatuses.some((s) => s === "failed");
  const hasError =
    email.copyStatus === "failed" || anyBlockCopyFailed || anyImageFailed;

  // A single in-flight block (the regenerate-one-block flow flips just that
  // block's copy status to "pending") must keep the email reading as
  // "generating" even when the whole-email copyStatus is already "ready".
  const anyBlockCopyPending = Object.values(blockCopyStatus).some(
    (s) => s === "pending",
  );

  // --- Weighted units -------------------------------------------------------
  // Relative weights; the final percent is normalized over only the units this
  // email actually has, so a 3-block and a 7-block email both reach 100%.
  type Unit = { weight: number; done: boolean };
  const units: Unit[] = [];

  // 1) Voice brief — gates everything.
  units.push({ weight: 15, done: email.voiceBrief != null });

  // 2) Subject lines + body copy (the templated/restyle group). A body that's
  //    being regenerated (status "pending") is NOT done even though the subject
  //    lines from the prior run still exist, so the bar reflects the in-flight
  //    body rewrite instead of sitting at 100%.
  const subjectsBodyDone =
    blockCopyStatus["body"] === "ready" ||
    ((email.subjectLineVariants?.length ?? 0) > 0 &&
      blockCopyStatus["body"] !== "pending");
  units.push({ weight: 15, done: subjectsBodyDone });

  // 3) Hero copy.
  const heroCopyDone = blockCopyStatus["hero"] === "ready";
  units.push({ weight: 10, done: heroCopyDone });

  // 4) Hero image — done immediately when the hero variant is type-only (no
  //    image is ever kicked, so heroImageStatus stays null), else when the Fal
  //    render resolves.
  units.push({ weight: 10, done: isSettledOk(email.heroImageStatus) });

  // 5) Optional blocks present in THIS email — combined copy + image, split
  //    evenly across the 40-point band.
  if (optionalInstances.length > 0) {
    const per = 40 / optionalInstances.length;
    for (const id of optionalInstances) {
      const copyReady = blockCopyStatus[id] === "ready";
      const imgOk = isSettledOk(blockImageStatus(email, id));
      units.push({ weight: per, done: copyReady && imgOk });
    }
  }

  // 6) Final image finalization slice — done only when EVERY image slot is
  //    settled OK. A pending image holds it (still working); a failed image
  //    leaves it incomplete so the bar freezes below 100% on the error.
  units.push({ weight: 10, done: imageStatuses.every(isSettledOk) });

  const totalWeight = units.reduce((s, u) => s + u.weight, 0);
  const doneWeight = units.reduce((s, u) => s + (u.done ? u.weight : 0), 0);
  const rawPercent = totalWeight > 0 ? (doneWeight / totalWeight) * 100 : 100;
  const percent = Math.max(0, Math.min(100, Math.round(rawPercent)));

  // --- Phase label ----------------------------------------------------------
  // Intentionally generic: while any copy/image stage is still in flight the
  // bar shows a single neutral status rather than narrating each internal
  // pipeline stage. The terminal states (error / complete) get their own
  // explicit labels below.
  let phaseLabel = "Working…";

  // --- State ----------------------------------------------------------------
  // `complete` once copy is ready (or legacy/null) AND every image resolved.
  const copyDone = email.copyStatus === "ready" || email.copyStatus == null;
  let state: EmailProgressState;
  if (hasError) {
    state = "error";
    phaseLabel = "Something went wrong";
  } else if (copyDone && allImagesResolved && !anyBlockCopyPending) {
    state = "complete";
    phaseLabel = "Draft ready";
  } else {
    state = "generating";
  }

  // Any copy/image stage still in flight. Lets the UI keep polling while a
  // single block regenerates and know when a terminal error has fully settled.
  const pending =
    email.copyStatus === "pending" || anyBlockCopyPending || anyImagePending;

  return {
    percent: state === "complete" ? 100 : percent,
    phaseLabel,
    state,
    pending,
  };
}
